import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Utensils, Waves, Sparkles } from 'lucide-react';
import { useLang } from '../i18n';

/**
 * HomeSearchBar — big, glassy search input right below the hero.
 * Free-text search that opens /cerca?q=… (global search page).
 * Suggestion chips underneath offer one-tap shortcuts to popular queries.
 */
export default function HomeSearchBar() {
  const [q, setQ] = useState('');
  const nav = useNavigate();
  const { t, lang } = useLang();

  const submit = (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) return;
    nav(`/cerca?q=${encodeURIComponent(query)}`);
  };

  const goQuick = (text) => {
    setQ(text);
    nav(`/cerca?q=${encodeURIComponent(text)}`);
  };

  // Localized placeholder + quick chips
  const placeholders = {
    it: 'Cerca ristoranti, spiagge, cantine…',
    en: 'Search restaurants, beaches, wineries…',
    de: 'Restaurants, Strände, Weingüter…',
    fr: 'Restaurants, plages, caves…',
    es: 'Restaurantes, playas, bodegas…',
    zh: '搜索餐厅、海滩、酒庄……',
    ar: 'مطاعم، شواطئ، مصانع نبيذ…',
  };
  // Even shorter placeholder for very narrow phones (auto-swapped via CSS)
  const shortPlaceholders = {
    it: 'Cosa stai cercando?',
    en: 'What are you looking for?',
    de: 'Was suchst du?',
    fr: 'Que cherchez-vous ?',
    es: '¿Qué estás buscando?',
    zh: '你在找什么?',
    ar: 'ما الذي تبحث عنه؟',
  };
  const chips = {
    it: [
      { icon: Utensils, label: 'Ristoranti' },
      { icon: Waves, label: 'Spiagge' },
      { icon: MapPin, label: 'Cantine' },
      { icon: Sparkles, label: 'SPA' },
    ],
    en: [
      { icon: Utensils, label: 'Restaurants' },
      { icon: Waves, label: 'Beaches' },
      { icon: MapPin, label: 'Wineries' },
      { icon: Sparkles, label: 'SPA' },
    ],
  };
  const activeChips = chips[lang] || chips.it;
  const placeholder = placeholders[lang] || placeholders.it;
  const shortPlaceholder = shortPlaceholders[lang] || shortPlaceholders.it;
  // Track viewport for compact placeholder on very narrow screens
  const [isNarrow, setIsNarrow] = useState(false);
  React.useEffect(() => {
    const check = () => setIsNarrow(window.innerWidth < 380);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <section
      className="relative w-full pt-6 pb-4 sm:pt-10 sm:pb-6"
      data-testid="home-search-section"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <form onSubmit={submit} role="search">
          <div className="relative group">
            <div
              className="absolute inset-0 rounded-full blur-lg opacity-40 group-focus-within:opacity-70 transition-opacity"
              style={{ background: 'radial-gradient(ellipse at center, rgba(188,172,138,0.6), transparent 70%)' }}
              aria-hidden="true"
            />
            <div className="relative flex items-center gap-1.5 sm:gap-2 bg-black/70 backdrop-blur-md border border-white/15 rounded-full pl-4 sm:pl-5 pr-1.5 sm:pr-2 py-1.5 sm:py-2.5 focus-within:border-[color:var(--yup-primary)] transition-colors shadow-xl">
              <Search size={18} className="text-[color:var(--yup-primary)] shrink-0 sm:hidden" strokeWidth={2.2} />
              <Search size={20} className="text-[color:var(--yup-primary)] shrink-0 hidden sm:block" strokeWidth={2.2} />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={isNarrow ? shortPlaceholder : placeholder}
                aria-label="Cerca su YUP"
                className="flex-1 min-w-0 bg-transparent border-none outline-none text-white placeholder-white/40 text-[15px] sm:text-base py-2"
                data-testid="home-search-input"
                enterKeyHint="search"
                inputMode="search"
                autoCorrect="off"
                autoCapitalize="off"
              />
              <button
                type="submit"
                data-testid="home-search-submit"
                aria-label="Cerca"
                className="shrink-0 bg-[color:var(--yup-primary)] hover:brightness-110 active:scale-95 text-black font-semibold flex items-center justify-center transition-all
                           w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2 rounded-full"
              >
                {/* Icon on mobile, text on desktop */}
                <Search size={18} className="sm:hidden" strokeWidth={2.4} />
                <span className="hidden sm:inline text-sm">Cerca</span>
              </button>
            </div>
          </div>
        </form>

        {/* Quick-tap suggestion chips */}
        <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="home-search-chips">
          {activeChips.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.label}
                type="button"
                onClick={() => goQuick(c.label)}
                data-testid={`home-search-chip-${c.label.toLowerCase()}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-[color:var(--yup-primary)]/15 border border-white/10 hover:border-[color:var(--yup-primary)]/40 text-white/75 hover:text-white text-xs sm:text-sm transition-all"
              >
                <Icon size={13} className="text-[color:var(--yup-primary)]" />
                {c.label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
